import { useEffect, useRef, useState } from 'react';
import {
    ChatContainer, MainContainer, Message, MessageInput, MessageList,
} from '@chatscope/chat-ui-kit-react';
import moment from 'moment';
import { produce } from 'immer';
import { Image, Space } from 'antd';
import { useApiFetch } from '../../hooks/api/useApiFetch';
import { IAvitoChatInfo } from './chat';
import PusherService from '../../services/pusher';

interface IAvitoChatMessage {
    id: string;
    author_id: string;
    created: number;
    content: {
        text?: string;
        image?: {
            sizes: {
                '1280x960': string;
                '140x105': string;
                '32x32': string;
                '640x480': string;
            }
        }
        link?: {
            text: string;
            url: string;
            preview?: {
                title?: string;
                images?: {
                    '1280x960': string;
                    '140x105': string;
                    '32x32': string;
                    '640x480': string;
                }
            }
        }
    };
    type: string;
    direction: 'in' | 'out';
    isRead: boolean;
    read: number;
    sender?: {
        fio: string;
    };
}

const AvitoChatMessages = ({
    channel, chat, id, sendDisabled,
}: { channel: string; chat: IAvitoChatInfo; id: number; sendDisabled: boolean; }) => {
    const api = useApiFetch();
    const [messages, setMessages] = useState<IAvitoChatMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [showNextPage, setShowNextPage] = useState(false);
    const [newMessages, setNewMessages] = useState(false);
    const hasMore = useRef(false);
    const pusher = useRef(new PusherService());

    const getMessages = async () => {
        setLoading(true);
        const response = await api.fetchData<{ messages: IAvitoChatMessage[]; meta: { has_more: boolean; } }>(`/avito-chats/${id}/messages`, 'post', true, { page });
        if (response.data) {
            if (page > 1) {
                setMessages(produce(messages, (draft) => {
                    // @ts-ignore
                    draft.unshift(...response.data.messages.reverse());
                }));
            } else {
                setMessages(response.data.messages.reverse());
            }
            hasMore.current = response.data.meta.has_more;
        }
        setLoading(false);
        setShowNextPage(false);
    };

    const getNewMessages = async () => {
        if (newMessages) {
            const response = await api.fetchData<{ messages: IAvitoChatMessage[]; meta: { has_more: boolean; } }>(`/avito-chats/${id}/messages`, 'post', true, { count: 10, page: 1 });
            if (response.data) {
                setMessages(produce(messages, (draft) => {
                    // @ts-ignore
                    response.data.messages.reverse().forEach((message) => {
                        const index = draft.findIndex((el) => el.id === message.id);
                        if (index < 0) {
                            draft.push(message);
                        }
                    });
                }));
            }
        }
        setNewMessages(false);
    };

    const openSocket = () => {
        pusher.current.open(channel);
        pusher.current.registerMessageListener('new-message', () => setNewMessages(true));
    };

    useEffect(() => {
        getMessages();
    }, [page]);

    useEffect(() => {
        if (showNextPage) {
            setPage((prev) => prev + 1);
        }
    }, [showNextPage]);

    useEffect(() => {
        getNewMessages();
    }, [newMessages]);

    useEffect(() => {
        openSocket();
        return () => {
            pusher.current.close();
        };
    }, []);

    const getMessageStatus = (message: IAvitoChatMessage): string => {
        if (message.read) {
            return '✓✓';
        }
        return '✓';
    };

    const onMessageSend = async (message: string) => {
        setSendLoading(true);
        await api.fetchData(`/avito-chats/${id}/send`, 'post', true, { message });
        setSendLoading(false);
        if (page === 1) {
            getMessages();
        } else {
            setPage(1);
        }
    };

    const nextPage = () => {
        if (hasMore.current) {
            setShowNextPage(true);
        }
    };

    const renderMessage = (message: IAvitoChatMessage) => (
        <Message
            key={message.id}
            model={{
                message: message.content.text,
                sender: message.direction === 'in' ? message.sender?.fio : chat.client.name,
                direction: message.direction === 'in' ? 'incoming' : 'outgoing',
                position: 0,
            }}
        >
            <Message.Header>
                <Space className="w-100 justify-content-between">
                    <span>{message.direction === 'out' ? message.sender?.fio || chat.order_source.name : chat.client.name}</span>
                    <span>{moment.unix(message.created).format('DD.MM.YYYY HH:mm')}</span>
                </Space>
            </Message.Header>
            <Message.Footer>
                {message.direction === 'out' && (
                    <div className="d-flex flex-1 justify-content-end"><span>{getMessageStatus(message)}</span></div>
                )}
            </Message.Footer>
            {['image', 'link'].includes(message.type) && (
                <Message.CustomContent>
                    {message.type === 'image' && (
                        <Image.PreviewGroup>
                            <Image
                                src={message.content.image?.sizes['640x480']}
                                style={{ borderRadius: 10, width: 150 }}
                                preview={{ src: message.content.image?.sizes['1280x960'] }}
                            />
                        </Image.PreviewGroup>
                    )}
                    {message.type === 'link' && (
                        <Space direction="vertical">
                            {message.content.link?.preview?.images && (
                                <Image
                                    src={message.content.link?.preview?.images['640x480']}
                                    style={{ borderRadius: 10, width: 150 }}
                                    preview={{ src: message.content.link?.preview?.images['1280x960'] }}
                                />
                            )}
                            <a href={message.content.link?.url} target="_blank" rel="noreferrer">{message.content.link?.text}</a>
                        </Space>
                    )}
                </Message.CustomContent>
            )}
        </Message>
    );

    return (
        <MainContainer>
            <ChatContainer>
                <MessageList id="messages-list" style={{ background: '#ebebeb', height: 'calc(100vh - 450px)' }} loading={loading} onYReachStart={nextPage}>
                    {messages.map(renderMessage)}
                </MessageList>
                <MessageInput
                    disabled={loading || sendLoading || sendDisabled}
                    placeholder="Введите сообщение"
                    attachButton={false}
                    onSend={onMessageSend}
                    sendDisabled={loading || sendLoading || sendDisabled}
                    className="white-space-break"
                />
            </ChatContainer>
        </MainContainer>
    );
};

export default AvitoChatMessages;
